import api from '../../utils/api';

export type VendorManualRefundCaseStatus = 'PENDING' | 'IN_REVIEW' | 'COMPLETED' | 'REJECTED';
export type VendorManualRefundTriggerSource =
  | 'AGENCY_CANCELLATION'
  | 'USER_REQUEST'
  | 'SYSTEM'
  | 'OTHER';

export type VendorManualRefundRefundMath = {
  grossPaidAmount: number;
  penaltyAmount: number | null;
  userRefundAmount: number | null;
  vedikaShareAmount: number | null;
  vendorShareAmount: number | null;
  splitSum: number | null;
  splitDelta: number | null;
  splitOk: boolean | null;
  penaltyOk: boolean | null;
  penaltyVendorOk: boolean;
  penaltyVendorMessage: string | null;
  vendorNetAfterPenalty: number | null;
  completionReady: boolean;
};

export type VendorManualRefundCaseRow = {
  caseId: string;
  serviceType: string;
  serviceRefId: string;
  vendorId: string;
  userId: string;
  paymentId: string | null;
  grossPaidAmount: number;
  penaltyAmount: number | null;
  userRefundAmount: number | null;
  vedikaShareAmount: number | null;
  vendorShareAmount: number | null;
  status: VendorManualRefundCaseStatus;
  triggerSource: VendorManualRefundTriggerSource;
  cancellationReason: string | null;
  adminNote: string | null;
  metadata: Record<string, unknown> | null;
  resolvedAt: string | null;
  resolvedByAdminId: string | null;
  createdAt: string;
  updatedAt: string;
  serviceSnapshot?: Record<string, unknown> | null;
  refundMath: VendorManualRefundRefundMath;
  vendorRole?: number | null;
  vendorTypeLabel?: string | null;
};

export type ListVendorManualRefundCasesParams = {
  page?: number;
  limit?: number;
  status?: string;
  serviceType?: string;
  /** Vendor account role (matches Vendors.vendorRole): 1 Hospital, 2 Clinic, … */
  vendorRole?: string | number;
  vendorId?: string;
  userId?: string;
  triggerSource?: string;
  search?: string;
};

export async function fetchVendorManualRefundCases(params: ListVendorManualRefundCasesParams = {}) {
  const response = await api.get<{
    success: boolean;
    data: VendorManualRefundCaseRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>('/master-admin/vendor-manual-refund-cases', { params });
  return response.data;
}

export async function fetchVendorManualRefundCaseById(caseId: string) {
  const response = await api.get<{
    success: boolean;
    data: VendorManualRefundCaseRow & {
      user: { userId: string; name?: string; phone_number?: string; emailId?: string } | null;
      vendor: { vendorId: string; email?: string; phoneNumber?: string; vendorRole?: number } | null;
    };
  }>(`/master-admin/vendor-manual-refund-cases/${encodeURIComponent(caseId)}`);
  return response.data;
}

export type PatchVendorManualRefundCaseBody = {
  penaltyAmount?: number | null;
  userRefundAmount?: number | null;
  vedikaShareAmount?: number | null;
  vendorShareAmount?: number | null;
  adminNote?: string | null;
  status?: VendorManualRefundCaseStatus;
  /** Sets user=gross, vedika=0, vendor=0; send penaltyAmount (use 0 if none). With status COMPLETED, runs service refund hooks (e.g. ambulance). */
  applyPreset?: 'FULL_USER_REFUND';
};

export async function patchVendorManualRefundCase(caseId: string, body: PatchVendorManualRefundCaseBody) {
  const response = await api.patch<{ success: boolean; data: VendorManualRefundCaseRow }>(
    `/master-admin/vendor-manual-refund-cases/${encodeURIComponent(caseId)}`,
    body
  );
  return response.data;
}
